import Circle from "@arcgis/core/geometry/Circle";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import MapView from "@arcgis/core/views/MapView";
import { RefObject } from "react";
import { AppConfig } from "../../../AppConfig";
import { computeTrueNorth, getMapView, highlightFeature, unHighlightGraphic } from "../../../utils/arcgisUtils";
import { createSector, getRelationshipId, isLighthouse, sanitizeSectors } from "../../../utils/lighthouseUtils";
import { DefaultSectorValues, LighthouseData } from "../../../utils/lighthouseConstants";
import { drawSectors } from "./lighthouseDrawing";
import { getNewStatus, NFSStatus } from "../../../utils/nfsUtils";
import type FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import type { DragEvent } from "@arcgis/core/views/input/types";
import type { SceneViewGraphicHit, ViewHitTestResult } from "@arcgis/core/views/types";
import type { CIMSolidStroke, CIMLineSymbol } from "@arcgis/core/symbols/cim/types";
import type CIMSymbol from "@arcgis/core/symbols/CIMSymbol";
import type Layer from "@arcgis/core/layers/Layer";
import type { ResourceHandle } from "@arcgis/core/core/Handles";

export const sectorLineClickHandler = (
    view: MapView,
    currentLine: React.RefObject<Graphic | null>,
    selectedLine: React.RefObject<Graphic | null>
) => {
    const lighthouseEditingLayer = view.map?.findLayerById("FyrlyktEditing");

    return view.on("click", (event) => {
        if (!lighthouseEditingLayer) return;

        view.hitTest(event, { include: lighthouseEditingLayer }).then((response) => {
            const lines = response.results.filter(
                (result) => (result as SceneViewGraphicHit).graphic.attributes?.objectType === "line"
            ) as SceneViewGraphicHit[];

            if (lines.length === 0) {
                currentLine.current = null;
                return;
            }

            const clickedLine = lines[0].graphic;
            selectedLine.current = clickedLine;
        });
    });
};

interface LighthouseHandlerPointerMoveParams {
    view: MapView;
    currentLine: React.RefObject<Graphic | null>;
    isDraggingLine: React.RefObject<boolean>;
    isSectorEditorOpenRef: React.RefObject<boolean>;
    lighthouseData: React.RefObject<LighthouseData>;
    mouseHoverHighlightRef: React.RefObject<Graphic | null>;
    selectedLine: React.RefObject<Graphic | null>;
    selectedFeatureRef: React.RefObject<Graphic | null>;
}

export const lighthouseHandlerPointerMove = (params: LighthouseHandlerPointerMoveParams) => {
    const {
        view,
        currentLine,
        isDraggingLine,
        isSectorEditorOpenRef,
        lighthouseData,
        mouseHoverHighlightRef,
        selectedLine,
        selectedFeatureRef,
    } = params;
    const lighthouseEditingLayer = view.map?.findLayerById("FyrlyktEditing");

    return view.on("pointer-move", (event) => {
        if (!isSectorEditorOpenRef.current) return; // Do not execute if sector editor is not open
        if (!view.container) return;
        // Refresh overlay graphics
        const overlayGraphicsLayer = view.map?.findLayerById("lighthouseOverlayLayer") as GraphicsLayer;
        if (overlayGraphicsLayer) {
            overlayGraphicsLayer.removeAll();
        }
        const excluded = [lighthouseEditingLayer];
        if (overlayGraphicsLayer) {
            excluded.push(overlayGraphicsLayer);
        }
        view.hitTest(event, { exclude: excluded.filter((layer) => layer != null) }).then(() => {
            if (!isDraggingLine.current) currentLine.current = null;
        });

        const includeLayers = [lighthouseEditingLayer, overlayGraphicsLayer].filter(
            (layer): layer is Layer => layer != null
        );

        const sectorRoseView = getMapView("sector-rose-map");
        const sectorRoseLayer = sectorRoseView?.map?.findLayerById("SectorRose") as GraphicsLayer;

        view.hitTest(event, { include: includeLayers }).then((response) => {
            handleHitTestResponse({
                view,
                response,
                currentLine,
                isDragging: isDraggingLine,
                lighthouseData,
                mouseHoverHighlightRef,
                selectedLine,
                selectedFeatureRef,
            });
            if (response.results.length === 0) {
                if (mouseHoverHighlightRef?.current) {
                    unHighlightGraphic(mouseHoverHighlightRef.current, sectorRoseLayer, "sector-rose-map").then(() => {
                        mouseHoverHighlightRef.current = null;
                    });
                }
            }
        });
    });
};

interface HandleHitTestResponseParams {
    view: MapView;
    response: ViewHitTestResult;
    currentLine: React.RefObject<Graphic | null>;
    isDragging: React.RefObject<boolean>;
    lighthouseData: React.RefObject<LighthouseData>;
    mouseHoverHighlightRef: React.RefObject<Graphic | null>;
    selectedLine: React.RefObject<Graphic | null>;
    selectedFeatureRef: React.RefObject<Graphic | null>;
}

const handleHitTestResponse = (params: HandleHitTestResponseParams) => {
    const {
        view,
        response,
        currentLine,
        isDragging,
        lighthouseData,
        mouseHoverHighlightRef,
        selectedLine,
        selectedFeatureRef,
    } = params;

    if (!view.container) return;
    const results = response.results;

    view.popupEnabled = results.length === 0;

    updateCurrentLine(results as SceneViewGraphicHit[], currentLine, selectedLine, isDragging);
    addOverlayGraphics({
        view,
        results: results as SceneViewGraphicHit[],
        isDragging,
        lighthouseData,
        mouseHoverHighlightRef,
        currentLine,
        selectedLine,
        selectedFeatureRef,
    });
};

const updateCurrentLine = (
    results: SceneViewGraphicHit[],
    currentLine: React.RefObject<Graphic | null>,
    selectedLine: React.RefObject<Graphic | null>,
    isDragging: React.RefObject<boolean>
) => {
    if (!results || results.length === 0) return;

    const lines = results.filter((result) => result.graphic.attributes?.objectType === "line");

    if (lines.length > 0 && !isDragging.current) {
        currentLine.current = lines[0].graphic ?? null;
    } else if (!isDragging.current && !selectedLine.current) {
        currentLine.current = null;
    }
};

const addOverlayLineGraphics = async (
    view: MapView,
    currentLine: React.RefObject<Graphic | null>,
    selectedLine: React.RefObject<Graphic | null>,
    mouseHoverHighlightRef: React.RefObject<Graphic | null>
) => {
    let newGraphic: Graphic | null = null;

    if (currentLine.current) {
        newGraphic = currentLine.current.clone();
    } else if (selectedLine.current) {
        newGraphic = selectedLine.current.clone();
    } else {
        return;
    }

    const overlayGraphicsLayer = view.map?.findLayerById("lighthouseOverlayLayer") as GraphicsLayer;

    const dragHandleGraphic = new Graphic({
        geometry: new Circle({
            center: new Point({
                x: (newGraphic.geometry as Polyline).paths[0][1][0],
                y: (newGraphic.geometry as Polyline).paths[0][1][1],
                spatialReference: view.spatialReference,
            }),
            radius: 10,
            spatialReference: view.spatialReference,
            geodesic: false,
        }),
        symbol: new SimpleMarkerSymbol({
            style: "circle",
            color: [0, 0, 0, 1],
            size: 10,
        }),
        attributes: {
            objectType: "dragHandle",
        },
    });
    if ((newGraphic?.symbol as CIMSymbol)?.data) {
        ((newGraphic?.symbol as CIMSymbol)?.data?.symbol as CIMLineSymbol)?.symbolLayers?.forEach(
            (layer) => {
                (layer as CIMSolidStroke).width = 5;
            }
        );
    } else {
        (newGraphic.symbol as SimpleLineSymbol).width = 5;
    }
    overlayGraphicsLayer.add(newGraphic);
    overlayGraphicsLayer.add(dragHandleGraphic);

    const sectorRoseView = getMapView("sector-rose-map");
    if (!sectorRoseView?.map) return;

    const sectorRoseLayer = sectorRoseView.map?.findLayerById("SectorRose") as GraphicsLayer;
    const correspondingLine = sectorRoseLayer.graphics.find(
        (graphic) =>
            graphic.attributes?.objectType === "line" && graphic.attributes?.index === newGraphic.attributes?.index
    );

    if (correspondingLine?.attributes.sectorId !== mouseHoverHighlightRef.current?.attributes.sectorId) {
        await highlightFeature(correspondingLine, "default", sectorRoseLayer, "sector-rose-map");
        await unHighlightGraphic(mouseHoverHighlightRef.current, sectorRoseLayer, "sector-rose-map");
        if (correspondingLine) mouseHoverHighlightRef.current = correspondingLine;
    }
};

const addOverlaySectorGraphics = async (
    view: MapView,
    lighthouseData: RefObject<LighthouseData>,
    results: SceneViewGraphicHit[],
    mouseHoverHighlightRef: React.RefObject<Graphic | null>,
    selectedFeatureRef: React.RefObject<Graphic | null>
) => {
    const sectorData = lighthouseData.current.sectors.find(
        (sector) => sector.globalid === results[0].graphic.attributes?.globalid
    );
    if (!sectorData) return;

    const overlayGraphicsLayer = view.map?.findLayerById("lighthouseOverlayLayer") as GraphicsLayer;

    const innerRadius = sectorData.innerRadius ?? DefaultSectorValues.defaultInnerRadius;
    const outerRadius = innerRadius + AppConfig.Lighthouse.DefaultSectorThickness;
    const center = lighthouseData.current.center;

    const firstAngle = lighthouseData.current.sectors[0].startAngle ?? 0;
    let startAngle = sectorData.startAngle ?? firstAngle;
    const endAngle =
        lighthouseData.current.sectors[(sectorData.index + 1) % lighthouseData.current.sectors.length]?.startAngle ??
        360 + firstAngle;

    if (startAngle > endAngle) {
        startAngle -= 360;
    }

    const trueNorthCorrection = await computeTrueNorth(lighthouseData.current.center);

    const labelPoint = new Point({
        x:
            center.x +
            ((outerRadius + innerRadius) / 2) *
            -Math.cos((-(startAngle - trueNorthCorrection + (endAngle - startAngle) / 2 + 270) * Math.PI) / 180),
        y:
            center.y +
            ((outerRadius + innerRadius) / 2) *
            -Math.sin((-(startAngle - trueNorthCorrection + (endAngle - startAngle) / 2 + 270) * Math.PI) / 180),
        spatialReference: lighthouseData.current.center.spatialReference,
    });

    if (isLighthouse(selectedFeatureRef.current) || lighthouseData.current.sectors.length < 2) {
        const plusSign = new Graphic({
            geometry: labelPoint,
            symbol: new SimpleMarkerSymbol({
                style: "cross",
                color: [0, 0, 0, 1],
                size: 10,
                outline: new SimpleLineSymbol({ color: [0, 0, 0, 1], width: 2 }),
            }),
            attributes: {
                objectType: "addSectorSymbol",
                index: results[0].graphic.attributes?.index,
                id: results[0].graphic.attributes?.id,
            },
        });
        overlayGraphicsLayer.add(plusSign);
    }

    const sectorRoseView = getMapView("sector-rose-map");
    if (!sectorRoseView?.map) return;

    const sectorRoseLayer = sectorRoseView.map?.findLayerById("SectorRose") as GraphicsLayer;
    const correspondingSector = sectorRoseLayer.graphics.find(
        (graphic) => graphic.attributes?.objectType === "sector" && graphic.attributes?.index === sectorData.index
    );

    if (correspondingSector?.attributes.globalid !== mouseHoverHighlightRef.current?.attributes.globalid) {
        await highlightFeature(correspondingSector, "default", sectorRoseLayer, "sector-rose-map");
        await unHighlightGraphic(mouseHoverHighlightRef.current, sectorRoseLayer, "sector-rose-map");
        if (correspondingSector) mouseHoverHighlightRef.current = correspondingSector;
    }
};

interface AddOverlayGraphicsParams {
    view: MapView;
    results: SceneViewGraphicHit[];
    isDragging: React.RefObject<boolean>;
    lighthouseData: RefObject<LighthouseData>;
    mouseHoverHighlightRef: React.RefObject<Graphic | null>;
    currentLine: React.RefObject<Graphic | null>;
    selectedLine: React.RefObject<Graphic | null>;
    selectedFeatureRef: React.RefObject<Graphic | null>;
}

const addOverlayGraphics = async (params: AddOverlayGraphicsParams) => {
    const {
        view,
        results,
        isDragging,
        lighthouseData,
        mouseHoverHighlightRef,
        currentLine,
        selectedLine,
        selectedFeatureRef,
    } = params;

    if (!selectedLine.current && (!results || results.length === 0) && !isDragging.current) return;

    if (currentLine.current || selectedLine.current) {
        addOverlayLineGraphics(view, currentLine, selectedLine, mouseHoverHighlightRef);
    } else if (!currentLine.current && !isDragging.current) {
        addOverlaySectorGraphics(view, lighthouseData, results, mouseHoverHighlightRef, selectedFeatureRef);
    }
};

const calculateAngle = (geometry: Polyline): number => {
    const lineCoords = geometry.paths[0];
    const angle = Math.atan2(lineCoords[1][1] - lineCoords[0][1], lineCoords[1][0] - lineCoords[0][0]);
    return ((angle * 180) / Math.PI + 360) % 360;
};

const setCursorStyle = (
    view: MapView,
    angleDeg: number,
    currentLine: React.RefObject<Graphic | null>,
    selectedLine: React.RefObject<Graphic | null>
) => {
    if (!view.container) return;

    if (
        !selectedLine.current ||
        selectedLine.current?.attributes.sectorId !== currentLine.current?.attributes.sectorId
    ) {
        view.container.style.cursor = "pointer";
        return;
    }

    if (angleDeg <= 22.5 || angleDeg > 337.5 || (angleDeg > 157.5 && angleDeg <= 202.5)) {
        view.container.style.cursor = "ns-resize";
    } else if ((angleDeg > 22.5 && angleDeg <= 67.5) || (angleDeg > 202.5 && angleDeg <= 247.5)) {
        view.container.style.cursor = "nwse-resize";
    } else if ((angleDeg > 67.5 && angleDeg <= 112.5) || (angleDeg > 247.5 && angleDeg <= 292.5)) {
        view.container.style.cursor = "ew-resize";
    } else if ((angleDeg > 112.5 && angleDeg <= 157.5) || (angleDeg > 292.5 && angleDeg <= 337.5)) {
        view.container.style.cursor = "nesw-resize";
    }
};

export const lighthouseOverlayHandler = (
    view: MapView,
    isSectorEditorOpen: React.RefObject<boolean>,
    dragHandleHover: React.RefObject<boolean>,
    isDraggingSectorLine: React.RefObject<boolean>,
    currentLine: React.RefObject<Graphic | null>,
    selectedLine: React.RefObject<Graphic | null>
) => {
    return view.on("pointer-move", (event) => {
        if (!isSectorEditorOpen.current) return; // Do not execute if sector editor is not open

        const overlayGraphicsLayer = view.map?.findLayerById("lighthouseOverlayLayer") as GraphicsLayer;
        view.hitTest(event, { include: overlayGraphicsLayer }).then((response) => {
            if (!view.container) return;

            const resultDragHandle = response.results.filter(
                (result) => (result as SceneViewGraphicHit).graphic.attributes?.objectType === "dragHandle"
            ) as SceneViewGraphicHit[];
            const resultSectorLine = response.results.filter(
                (result) => (result as SceneViewGraphicHit).graphic.attributes?.objectType === "line"
            ) as SceneViewGraphicHit[];
            const resultAddSector = response.results.filter(
                (result) => (result as SceneViewGraphicHit).graphic.attributes?.objectType === "addSectorSymbol"
            ) as SceneViewGraphicHit[];

            if (!isDraggingSectorLine.current) {
                dragHandleHover.current = resultDragHandle.length > 0;
            }

            if (resultDragHandle.length === 0 && resultSectorLine.length > 0) {
                const angle = calculateAngle(resultSectorLine[0].graphic.geometry as Polyline);
                setCursorStyle(view, angle, currentLine, selectedLine);
            } else if (resultDragHandle.length > 0 && resultSectorLine.length > 0) {
                const angle = calculateAngle(resultSectorLine[0].graphic.geometry as Polyline);
                setCursorStyle(view, angle + 90, currentLine, selectedLine);
            } else if (resultAddSector && resultAddSector.length > 0) {
                view.container.style.cursor = "pointer";
            } else {
                view.container.style.cursor = "default";
            }
        });
    });
};

export const lighthouseHandlerClickSector = (
    view: MapView,
    setLighthouseSectorPanelsOpen: ((value: Set<number>) => void) | undefined,
    isSectorEditTableOpen: React.RefObject<boolean>
) => {
    const lighthouseEditingLayer = view.map?.layers.find((layer) => layer.id === "FyrlyktEditing");

    return view.on("click", (event) => {
        if (isSectorEditTableOpen.current) return; // The table holds the sectors while it is open

        view.hitTest(event, { include: lighthouseEditingLayer }).then((response) => {
            const result = response.results.filter(
                (result) => (result as SceneViewGraphicHit).graphic.attributes?.objectType === "sector"
            );

            if (result.length === 0) return;

            const sectorIndex = (result[0] as SceneViewGraphicHit).graphic.attributes.index;
            setLighthouseSectorPanelsOpen!(new Set([sectorIndex]));
        });
    });
};

export const lighthouseHandlerAddSector = (
    view: MapView,
    lighthouseData: RefObject<LighthouseData>,
    setLighthouseData: ((value: LighthouseData) => void) | undefined,
    isSectorEditTableOpen: React.RefObject<boolean>
) => {
    return view.on("click", (event) => {
        if (isSectorEditTableOpen.current) return; // The table holds the sectors while it is open

        const overlayGraphicsLayer = view.map?.findLayerById("lighthouseOverlayLayer") as GraphicsLayer;
        const include = [];
        if (overlayGraphicsLayer) {
            include.push(overlayGraphicsLayer.graphics);
        }
        view.hitTest(event, { include: include }).then((response) => {
            const result = response.results.filter(
                (result) => (result as SceneViewGraphicHit).graphic.attributes?.objectType === "addSectorSymbol"
            );

            if (result.length === 0) return;

            const newLighthouseData = createSector(
                lighthouseData.current,
                (result[0] as SceneViewGraphicHit).graphic.attributes.index
            );
            lighthouseData.current = newLighthouseData;
            setLighthouseData!({ ...lighthouseData.current });

            // Remove the plus sign after adding a sector
            if (overlayGraphicsLayer) {
                overlayGraphicsLayer.remove((result[0] as SceneViewGraphicHit).graphic);
            }
        });
    });
};

export const lighthouseHandlerPointerEditingLayer = (
    view: MapView,
    isDraggingLine: React.RefObject<boolean>,
    isSectorEditorOpen: React.RefObject<boolean>
) => {
    const lighthouseEditingLayer = view.map?.layers.find((layer) => layer.id === "FyrlyktEditing");
    let highlight: ResourceHandle | void | null = null;

    return view.on("pointer-move", (event) => {
        if (!isSectorEditorOpen.current) return; // Do not execute if sector editor is not open

        view.hitTest(event, { include: lighthouseEditingLayer }).then((response) => {
            const resultsSector = response.results.filter(
                (result) => (result as SceneViewGraphicHit).graphic.attributes?.objectType === "sector"
            );
            const resultsSectorLine = response.results.filter(
                (result) => (result as SceneViewGraphicHit).graphic.attributes?.objectType === "line"
            );

            if (highlight) {
                highlight.remove();
            }

            if (resultsSector.length > 0 && resultsSectorLine.length === 0 && !isDraggingLine.current) {
                if (view.container) {
                    view.container.style.cursor = "pointer";
                }
                highlightFeature(
                    (resultsSector[0] as SceneViewGraphicHit).graphic,
                    "default",
                    lighthouseEditingLayer
                ).then((result) => {
                    highlight = result;
                });
            } else if (resultsSector.length === 0 && resultsSectorLine.length === 0 && view.container) {
                view.container.style.cursor = "default";
            }
        });
    });
};

interface DragHandlerParams {
    view: MapView;
    currentLine: React.RefObject<Graphic | null>;
    selectedLine: React.RefObject<Graphic | null>;
    temporaryLine: React.RefObject<Graphic | null>;
    dragHandleHover: React.RefObject<boolean>;
    lighthouseData: React.RefObject<LighthouseData | null>;
    setLighthouseData: ((value: LighthouseData) => void) | undefined;
    isDraggingLine: React.RefObject<boolean>;
    setIsEditing: ((value: boolean) => void) | undefined;
    trueNorth: React.RefObject<number>;
    sectorOpacity: React.RefObject<number>;
    showSectorColors: React.RefObject<boolean>;
    lighthouseTableSelectedSector: React.RefObject<number | null>;
    mouseHoverHighlightRef: React.RefObject<Graphic | null>;
}

const dragLogic = (event: DragEvent, params: DragHandlerParams) => {
    const {
        view,
        currentLine,
        selectedLine,
        temporaryLine,
        dragHandleHover,
        lighthouseData,
        setLighthouseData,
        isDraggingLine,
        setIsEditing,
        trueNorth,
        sectorOpacity,
        showSectorColors,
        lighthouseTableSelectedSector,
        mouseHoverHighlightRef,
    } = params;

    if (currentLine.current?.attributes.sectorId !== selectedLine.current?.attributes.sectorId) return;

    event.stopPropagation();

    setIsEditing!(true);

    const lighthouseLayer = view.map?.layers.find((layer) => layer.id === "FyrlyktEditing") as GraphicsLayer;
    let overlayGraphicsLayer = view.map?.findLayerById("lighthouseOverlayLayer") as GraphicsLayer;

    if (!overlayGraphicsLayer) {
        overlayGraphicsLayer = new GraphicsLayer({
            id: "lighthouseOverlayLayer",
            title: "Overlay Graphics",
            listMode: "hide",
        });
        view.map?.add(overlayGraphicsLayer);
    }

    const data = lighthouseData.current!;

    // Stop dragging in the map if we are dragging a line
    const action = event.action;

    if (currentLine.current) {
        isDraggingLine.current = true;

        temporaryLine.current ??= new Graphic({
            geometry: currentLine.current?.geometry,
            attributes: { objectType: "temporaryLine" },
            symbol: new SimpleLineSymbol({
                color: [0, 0, 0, 1],
                width: 1,
                style: "dash",
            }),
        });
        if (temporaryLine.current) {
            overlayGraphicsLayer.add(temporaryLine.current);
        }

        // Compute the new line length based on cursor position
        const endPoint = view.toMap({ x: event.x, y: event.y });
        const geometry = currentLine.current.geometry as Polyline;

        const currentLineId = currentLine.current.attributes.sectorId;
        const currentItem = data.sectors.find((sector) => sector.globalid === currentLineId);

        if (!currentItem) return;

        if (dragHandleHover.current) {
            currentItem.startLineLength = Math.sqrt(
                Math.pow(endPoint.x - geometry.paths[0][0][0], 2) + Math.pow(endPoint.y - geometry.paths[0][0][1], 2)
            );
        } else {
            const rotationOffset = 270;

            const newAngle =
                (-Math.atan2(endPoint.y - geometry.paths[0][0][1], endPoint.x - geometry.paths[0][0][0]) * 180) /
                Math.PI +
                rotationOffset +
                trueNorth.current;
            currentItem.startAngle = newAngle;
        }

        const currentIndex = currentItem.index;

        // Sanitize all sectors to maintain proper ordering, line lengths, and angles
        sanitizeSectors(data, currentIndex);

        lighthouseData.current = data;

        const newLineGeometry = lighthouseLayer.graphics.find(
            (graphic) =>
                graphic.attributes?.objectType === "line" &&
                graphic.attributes?.sectorId === currentLine.current?.attributes?.sectorId
        );
        if (newLineGeometry) {
            currentLine.current = newLineGeometry.clone();
            selectedLine.current = newLineGeometry.clone();
        }

        endDragLogic({
            view,
            action,
            currentLine,
            selectedLine,
            temporaryLine,
            lighthouseLayer,
            isDraggingLine,
            setIsEditing: setIsEditing!,
            lighthouseData,
            setLighthouseData,
            mouseHoverHighlightRef,
        });
    }

    const showAllSectors = showSectorColors.current
        ? Array(lighthouseData.current?.sectors.length)
            .fill(0)
            .map((_, i) => i)
        : [];

    drawSectors({
        graphicsLayer: lighthouseLayer,
        data: lighthouseData.current!,
        setIsEditing: setIsEditing!,
        sectorsToFill:
            lighthouseTableSelectedSector.current !== null ? [lighthouseTableSelectedSector.current] : showAllSectors,
        opacity: sectorOpacity.current,
        drawNumbers: false,
        trueNorthCorrection: trueNorth.current,
    });
};

interface EndDragLogicParams {
    view: MapView;
    action: string;
    currentLine: React.RefObject<Graphic | null>;
    selectedLine: React.RefObject<Graphic | null>;
    temporaryLine: React.RefObject<Graphic | null>;
    lighthouseLayer: GraphicsLayer;
    isDraggingLine: React.RefObject<boolean>;
    setIsEditing: (value: boolean) => void;
    lighthouseData: React.RefObject<LighthouseData | null>;
    setLighthouseData: ((value: LighthouseData) => void) | undefined;
    mouseHoverHighlightRef: React.RefObject<Graphic | null>;
}

const endDragLogic = async (params: EndDragLogicParams) => {
    const {
        view,
        action,
        currentLine,
        selectedLine,
        temporaryLine,
        lighthouseLayer,
        isDraggingLine,
        setIsEditing,
        lighthouseData,
        setLighthouseData,
        mouseHoverHighlightRef,
    } = params;

    if (action === "end") {
        setIsEditing(false);

        if (temporaryLine.current) {
            lighthouseLayer.remove(temporaryLine.current);
            temporaryLine.current = null;
        }

        lighthouseData.current?.sectors.sort((a, b) => {
            return a.startAngle! - b.startAngle!;
        });

        setLighthouseData?.({ ...lighthouseData.current! });
        mouseHoverHighlightRef.current = currentLine.current;

        const sectorLayer = view.map?.findLayerById("Sektorflate") as FeatureLayer;
        const sectorTable = view.map?.findTableById("Lyssektor") as FeatureLayer;

        if (selectedLine.current) {
            const sectorEntry = await sectorTable.queryFeatures({
                where: `globalid = '${selectedLine.current?.attributes.sectorId}'`,
                outFields: ["*"],
            });
            const sectorCount = lighthouseData.current!.sectors.length;

            const sectorEntryId = sectorEntry.features[0].attributes.objectid;

            await sectorTable.load();
            await sectorLayer.load();

            const sector = await sectorTable.queryRelatedFeatures({
                outFields: ["*"],
                relationshipId: getRelationshipId(sectorLayer),
                objectIds: [sectorEntryId],
                returnGeometry: true,
                geometryPrecision: 20,
            });

            const sectorIndex = lighthouseData.current?.sectors.findIndex(
                (s) => s.globalid === sector[sectorEntryId]?.features[0].attributes.fksektorid
            );

            if (sectorIndex !== undefined) {
                const previousSectorIndex = (sectorIndex - 1 + sectorCount) % sectorCount;
                lighthouseData.current!.sectors[sectorIndex].nfs_status = getNewStatus(
                    lighthouseData.current!.sectors[sectorIndex].nfs_status,
                    NFSStatus.PROJECTED_EXISTING
                );
                lighthouseData.current!.sectors[sectorIndex].nfs_status_line1 = getNewStatus(
                    lighthouseData.current!.sectors[sectorIndex].nfs_status_line1,
                    NFSStatus.PROJECTED_EXISTING
                );
                lighthouseData.current!.sectors[previousSectorIndex].nfs_status = getNewStatus(
                    lighthouseData.current!.sectors[previousSectorIndex].nfs_status,
                    NFSStatus.PROJECTED_EXISTING
                );
                lighthouseData.current!.sectors[previousSectorIndex].nfs_status_line2 = getNewStatus(
                    lighthouseData.current!.sectors[previousSectorIndex].nfs_status_line2,
                    NFSStatus.PROJECTED_EXISTING
                );
            }
        }

        currentLine.current = null;
        selectedLine.current = null;
        isDraggingLine.current = false;
    }
};

export const lighthouseHandlerDrag = ({
    view,
    currentLine,
    selectedLine,
    temporaryLine,
    dragHandleHover,
    lighthouseData,
    setLighthouseData,
    isDraggingLine,
    setIsEditing,
    trueNorth,
    sectorOpacity,
    showSectorColors,
    lighthouseTableSelectedSector,
    mouseHoverHighlightRef,
}: DragHandlerParams) => {
    return view.on("drag", (event) => {
        if (currentLine.current || isDraggingLine.current) {
            dragLogic(event, {
                view,
                currentLine,
                selectedLine,
                temporaryLine,
                dragHandleHover,
                lighthouseData,
                setLighthouseData,
                isDraggingLine,
                setIsEditing,
                trueNorth,
                sectorOpacity,
                showSectorColors,
                lighthouseTableSelectedSector,
                mouseHoverHighlightRef,
            });
        } else {
            isDraggingLine.current = false;
        }

        if (event.action === "end") {
            isDraggingLine.current = false;
        }
    });
};
